import { apiClient } from '@kss/api';

/**
 * POST document line-item rows ONE AT A TIME (not `Promise.all`).
 *
 * Why: posting all lines in a parallel burst raced the apiClient's
 * auth-refresh interceptor — when the token expired mid-burst, the in-flight
 * requests 401'd and (because each `.catch`-swallowed its error) were silently
 * dropped. Invoices then showed only a subset of their products. Posting
 * sequentially lets the token refresh settle between calls, and a single retry
 * covers a transient blip. The separate *Line table is the authoritative store,
 * so getting every row in matters.
 *
 * Returns how many of `payloads` were saved (callers can warn on a shortfall).
 */
export async function postLinesSequential(
  path: string,
  payloads: Array<Record<string, unknown>>,
): Promise<number> {
  let ok = 0;
  for (const body of payloads) {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        await apiClient.post(path, body);
        ok++;
        break;
      } catch {
        if (attempt === 0) await new Promise((r) => setTimeout(r, 150));
      }
    }
  }
  return ok;
}

/**
 * POST a document header and RELIABLY return its primary id (+ code).
 *
 * Why this exists: nearly every create endpoint (`POST /sales/SalesInvoice`,
 * `/sales/SalesOrder`, `/purchase/PurchaseOrder`, `/crm/Quotation`,
 * `/restaurant/RestaurantOrder`, …) returns `200 OK` with an EMPTY body — the
 * created row (and its `id`) is NOT echoed (confirmed against the live swagger,
 * whose response schema is a bare `{"200":{"description":"OK"}}`). The composers
 * read `r.data?.id`, get `undefined`, and then SKIP the `if (id != null)` block
 * that posts the line-item rows — so a freshly created invoice/order persists
 * ZERO line rows (only the capped `description` JSON survives → the "4 of 25
 * products" bug).
 *
 * Fix: when the POST body carries no id, recover it with a TARGETED filtered
 * GET on the entity's unique field — `GET {path}?{matchField}={value}` returns
 * just the matching row (confirmed live: `?invoiceNumber=SI-260620-0001` → 1
 * row). We pick the highest id among matches (covers a rare same-number race)
 * and fall back to a full-list scan if the backend ignores the filter.
 *
 * `matchField` is the UNIQUE column to recover by. Default `'code'`; pass
 * `'invoiceNumber'`/`'orderNumber'`/etc. for entities whose `code` is blank
 * (SalesInvoice/POS store the number in `invoiceNumber`, not `code`). Recovery
 * REFUSES to guess when the match value is empty (returns `undefined`) rather
 * than risk attaching lines to a foreign row.
 *
 * The real backend fix is to return the created row; until then this is the
 * load-bearing workaround. See doc/BACKEND_GAPS.md.
 */
export async function createHeaderAndGetId(
  path: string,
  payload: Record<string, unknown>,
  opts: { matchField?: string; matchValue?: string | number } = {},
): Promise<{ id: number | string | undefined; code: string | undefined }> {
  const matchField = opts.matchField ?? 'code';
  const matchValue = opts.matchValue ?? (payload[matchField] as string | number | undefined);

  const r = await apiClient.post(path, payload);

  // 1) Happy path: the backend echoed the row ({data:{...}} or {...}).
  const top = (r?.data ?? {}) as Record<string, unknown>;
  const node = (((top.data as Record<string, unknown>) ?? top) ?? {}) as Record<string, unknown>;
  const direct = node.id;
  if (typeof direct === 'number' || typeof direct === 'string') {
    return { id: direct, code: (node.code as string) ?? (matchValue != null ? String(matchValue) : undefined) };
  }

  // 2) Empty body: recover by the unique field. Never guess on an empty key.
  if (matchValue == null || String(matchValue).trim() === '') {
    return { id: undefined, code: undefined };
  }
  const rowsOf = (data: unknown): Array<Record<string, unknown>> => {
    const b = (data ?? {}) as Record<string, unknown>;
    return (Array.isArray(b) ? b
      : Array.isArray(b.data) ? b.data
      : Array.isArray((b.data as Record<string, unknown>)?.items) ? (b.data as { items: unknown[] }).items
      : Array.isArray(b.items) ? b.items
      : []) as Array<Record<string, unknown>>;
  };
  const pick = (rows: Array<Record<string, unknown>>) => {
    const matches = rows.filter((row) => String(row[matchField] ?? '') === String(matchValue));
    if (!matches.length) return undefined;
    matches.sort((a, b) => Number(b.id ?? 0) - Number(a.id ?? 0));
    return matches[0];
  };
  try {
    const q = `${path}?${encodeURIComponent(matchField)}=${encodeURIComponent(String(matchValue))}`;
    let win = pick(rowsOf((await apiClient.get(q)).data));
    if (!win) win = pick(rowsOf((await apiClient.get(path)).data)); // filter ignored → full scan
    if (win) return { id: win.id as number | string, code: (win.code as string) ?? String(matchValue) };
  } catch { /* recovery failed — fall through */ }
  return { id: undefined, code: String(matchValue) };
}
